import React, { useEffect, useRef } from 'react';

import { hasTextSelection } from '@copilot/ui';

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Memoized so an entry whose object reference hasn't changed (every
// transcript ENTRY other than the one just added/patched — see
// LiveMeetingPage's patchTranscript) skips re-rendering even though the
// containing `entries` array is a new array reference. Matters most for
// a long meeting with hundreds/thousands of transcript entries, where
// re-rendering the whole list on every unrelated update (e.g. the
// elapsed-timer tick) would be real, visible jank.
// Which capture produced a row. Unknown/legacy rows keep the original
// "Client" label.
function sourceLabel(audioSource) {
  if (audioSource === 'microphone') return 'You (microphone)';
  if (audioSource === 'system_audio') return 'Meeting audio';
  return 'Client';
}

const TranscriptEntry = React.memo(function TranscriptEntry({
  entry,
  onRetryTranslation,
  isSelected,
  onSelectQuestion,
  questionsOnly,
}) {
  // A row is a KrishvaQ-answer navigation target when it either is a
  // detected question or already has an AI answer in any state - and
  // only when the page wired up a handler. Plain transcript-only lines
  // stay inert, so the table does not become confusingly interactive.
  const selectable =
    !!onSelectQuestion &&
    (!!entry.isQuestion ||
      !!entry.answer ||
      !!entry.isGeneratingAnswer ||
      !!entry.answerError);

  // A click that ends a text selection is the user copying the line, not
  // choosing it - the row stays put and the selection survives.
  const select = selectable
    ? () => {
        if (!hasTextSelection()) onSelectQuestion(entry.id);
      }
    : undefined;

  // Enter and Space activate the row, matching a native button. Space is
  // prevented from also scrolling the transcript container.
  const handleKeyDown = selectable
    ? (event) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          onSelectQuestion(entry.id);
        }
      }
    : undefined;

  // The WHOLE row is the click target. It can't be a <button> (the
  // translation cell has its own "Retry translation" button, and nested
  // buttons are invalid), so it's an accessible role="button" instead.
  const interactiveProps = selectable
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-pressed': isSelected,
        'aria-label':
          (isSelected
            ? 'Showing KrishvaQ answer for question: '
            : 'Show KrishvaQ answer for question: ') +
          (entry.original || 'this question'),
        onClick: select,
        onKeyDown: handleKeyDown,
      }
    : { role: 'row' };

  return (
    <div
      className={
        'transcript-entry' +
        (selectable ? ' is-question' : '') +
        (selectable && isSelected ? ' is-selected' : '')
      }
      {...interactiveProps}
    >
      <div className="transcript-cell transcript-cell-original" role="cell">
        <div className="transcript-meta">
          <span>{sourceLabel(entry.audioSource)}</span>
          <span>{formatTime(entry.timestamp)}</span>
        </div>
        <p className="transcript-text">{entry.original}</p>
      </div>

      {/* An Interview lists only the detected questions: no second column. */}
      {!questionsOnly && (
        <div className="transcript-cell transcript-cell-translation" role="cell">
          {entry.translated ? (
            <>
              <div className="translation-label">Translation</div>
              <p className="transcript-text">{entry.translated}</p>
            </>
          ) : (
            <p className="transcript-empty-cell">No translation available</p>
          )}

          {entry.translationError && (
            <div role="status">
              {/*
                The backend's message says WHY this failed and whether
                waiting helps ("busy, clears in a few seconds" vs "quota
                has run out"). This used to render a hardcoded
                "Translation unavailable." and throw that away, leaving
                every cause looking identical.
              */}
              <p style={{ color: 'var(--color-danger)', fontSize: '0.85rem' }}>
                {entry.translationError}
              </p>
              {onRetryTranslation && (
                <button
                  type="button"
                  className="transcript-retry-button"
                  onClick={(event) => {
                    // Don't also select the row - this button has its own job.
                    event.stopPropagation();
                    onRetryTranslation(entry.id);
                  }}
                >
                  Retry translation
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const InterimTranscriptEntry = React.memo(function InterimTranscriptEntry({
  interimText,
  interimTranslation,
}) {
  return (
    <div className="transcript-entry interim" role="row">
      <div className="transcript-cell transcript-cell-original" role="cell">
        <div className="transcript-meta">Client · Live</div>
        <p className="transcript-text live-draft">{interimText}</p>
      </div>

      <div className="transcript-cell transcript-cell-translation" role="cell">
        {interimTranslation ? (
          <>
            <div className="translation-label live">Translation · Live</div>
            <p className="transcript-text live-draft">{interimTranslation}</p>
          </>
        ) : (
          <p className="transcript-empty-cell">Translation will appear here</p>
        )}
      </div>
    </div>
  );
});

function TranscriptTableHeader({ variant }) {
  return (
    <div className="transcript-table-header" role="row">
      <div role="columnheader">
        {variant === 'questions' ? 'Detected Question' : 'Meeting Audio'}
      </div>
      {variant !== 'questions' && <div role="columnheader">Transcription</div>}
    </div>
  );
}

function TranscriptPanel({
  entries,
  interimText,
  interimTranslation,
  autoScroll = true,
  onRetryTranslation,
  selectedQuestionId = null,
  onSelectQuestion,
  // 'full' (default): the whole transcript, beside its translation.
  // 'questions': an Interview, or a Meeting with Show Live Transcript off -
  // its speech recognition feeds the live AI answers - the caller passes the
  // detected questions and no full transcript is shown.
  variant = 'full',
}) {
  const endRef = useRef(null);

  // Keyed on entries.length (not the `entries` array itself): a
  // streaming AI answer replaces `transcriptsById` with a new object on
  // every single delta (see LiveMeetingPage's answer_delta handler),
  // which gives `entries` a new reference every few milliseconds even
  // though no row was added or removed. Depending on the array itself
  // re-queued a smooth scroll animation on every token of every
  // streaming answer - real, visible jank with no benefit, since the
  // streaming row is already the last one in view. Scrolling still
  // fires whenever a row is actually added/removed or the interim line
  // changes.
  useEffect(() => {
    if (!autoScroll) return;
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScroll, entries.length, interimText, interimTranslation]);

  // An Interview never shows live speech, only the questions detected from it.
  const questionsOnly = variant === 'questions';
  const liveText = questionsOnly ? '' : interimText;

  if (entries.length === 0 && !liveText) {
    return questionsOnly ? (
      <div className="empty-state" data-testid="interview-waiting">
        <div className="empty-state-title">Waiting for a question...</div>
        <p>
          KrishvaQ listens for questions and shows each detected one here with its
          answer. Audio is processed to provide AI assistance; no full transcript is
          shown.
        </p>
      </div>
    ) : (
      <div className="empty-state">
        <div className="empty-state-title">Waiting for speech...</div>
        <p>Transcript will appear here once the client starts speaking.</p>
      </div>
    );
  }

  return (
    <div
      className={'transcript-table' + (questionsOnly ? ' is-questions' : '')}
      role="table"
      aria-label={
        variant === 'questions' ? 'Detected interview questions' : 'Meeting transcript and transcription'
      }
    >
      <TranscriptTableHeader variant={variant} />

      <div role="rowgroup">
        {entries.map((entry) => (
          <TranscriptEntry
            entry={entry}
            key={entry.id}
            onRetryTranslation={onRetryTranslation}
            isSelected={entry.id === selectedQuestionId}
            onSelectQuestion={onSelectQuestion}
            questionsOnly={questionsOnly}
          />
        ))}

        {liveText && (
          <InterimTranscriptEntry
            interimText={liveText}
            interimTranslation={interimTranslation}
          />
        )}
      </div>

      <div ref={endRef} />
    </div>
  );
}

export default React.memo(TranscriptPanel);
